/**
 * Barcode / QR configuration and the scannable-code payload grammar.
 *
 * SINGLE SOURCE OF TRUTH — nothing about a printed or scanned code should be
 * hardcoded at a call site. Label sizes, QR appearance, the lost-pet landing
 * URL, id prefixes and route paths all live here so they can be retuned in one
 * place (or via env) without touching components.
 *
 * Every value that may differ per deployment reads from an env var first and
 * falls back to a safe default, so `.env.development` / `.env.production` can
 * override without a code change.
 */

/** Physical label sizes. `unit` feeds the printed `@page { size: ... }` rule. */
export type PaperUnit = "mm" | "cm" | "in" | "px";

export interface LabelPaper {
  width: number;
  height: number;
  unit: PaperUnit;
}

function envValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function envNumber(value: unknown, fallback: number): number {
  const parsed = Number(envValue(value));

  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Default label stock — the "normal barcode label" size used across the app
 * (see BarcodeLabelPrint.vue, which defaults to the same 80 x 50 mm sheet).
 * Override per deployment with VITE_LABEL_WIDTH_MM / VITE_LABEL_HEIGHT_MM.
 */
export const LABEL_PAPER_80X50: LabelPaper = {
  width: envNumber(import.meta.env.VITE_LABEL_WIDTH_MM, 80),
  height: envNumber(import.meta.env.VITE_LABEL_HEIGHT_MM, 50),
  unit: "mm",
};

/**
 * Item-definition label stock — 50 x 25 mm, the sticker roll used for warehouse
 * item barcodes (add/edit dialog, item list, item detail). Deliberately its own
 * constant rather than a retune of LABEL_PAPER_80X50: that sheet is still what
 * the service-room QR stickers are printed on, and the two rolls differ.
 * Override per deployment with VITE_ITEM_LABEL_WIDTH_MM / VITE_ITEM_LABEL_HEIGHT_MM.
 */
export const LABEL_PAPER_ITEM_50X25: LabelPaper = {
  width: envNumber(import.meta.env.VITE_ITEM_LABEL_WIDTH_MM, 50),
  height: envNumber(import.meta.env.VITE_ITEM_LABEL_HEIGHT_MM, 25),
  unit: "mm",
};

/**
 * Order label stock — 100 x 100 mm, the square sticker put on a packed order before it
 * goes out: who it is for, where it goes, what it costs, and a scannable order number.
 *
 * Its own constant rather than a retune of the other two: the 80x50 roll is still the QR
 * sticker stock and 50x25 is the shelf/item roll. All three are in use at once, on
 * different printers. Override per deployment with
 * VITE_ORDER_LABEL_WIDTH_MM / VITE_ORDER_LABEL_HEIGHT_MM.
 */
export const LABEL_PAPER_ORDER_100X100: LabelPaper = {
  width: envNumber(import.meta.env.VITE_ORDER_LABEL_WIDTH_MM, 100),
  height: envNumber(import.meta.env.VITE_ORDER_LABEL_HEIGHT_MM, 100),
  unit: "mm",
};

/**
 * Boarding label stock — 50 x 30 mm, the sticker that goes on a kennel, a carrier or a file:
 * a scannable booking id, the owner's name and the animal's name.
 *
 * Its own constant rather than a retune of the other three; all four rolls are in use at once
 * on different printers. Override per deployment with
 * VITE_BOARDING_LABEL_WIDTH_MM / VITE_BOARDING_LABEL_HEIGHT_MM.
 */
export const LABEL_PAPER_BOARDING_50X30: LabelPaper = {
  width: envNumber(import.meta.env.VITE_BOARDING_LABEL_WIDTH_MM, 50),
  height: envNumber(import.meta.env.VITE_BOARDING_LABEL_HEIGHT_MM, 30),
  unit: "mm",
};

/**
 * Barcode geometry for the boarding sticker. Same reasoning as `ITEM_LABEL_PRINT_PROPS` below,
 * recomputed for a 50 x 30 mm sheet — and, like those, NOT cosmetic.
 *
 * VERTICAL BUDGET. 30 mm is ~113 px of printable height, ~98 px usable after the 2 mm block
 * padding. The stack is owner 8.8 + pet 11 + the barcode block (bars 46 + 2x2 margin + 8 px
 * digits + jsbarcode's own 2 px text margin = 60) + two 2 px gaps ~= 84 px. `BarcodeLabelPrint`'s
 * 80 x 50 defaults (barHeight 60, fontSize 16) overflow this sheet outright.
 *
 * ⚠️ HORIZONTAL IS THE TIGHT ONE, and it is why `barWidth` is 1. 50 mm is only ~189 px, while a
 * CODE128 of a boarding name is ~200-233 modules — so even at width 1 the print CSS scales the
 * SVG down through `max-width:100%`, and scaling an SVG shrinks the bars' HEIGHT along with it.
 * `barHeight` is 46 rather than the item roll's 32 precisely so that after that ~0.85 scale the
 * bars still clear ~39 px (~10 mm), which is what a hand scanner reads reliably at an angle.
 * Raising `barWidth` to 2 doubles the module width, forces a ~0.45 scale, and drops the bars
 * under 6 mm — at which point the sticker stops scanning.
 */
export const BOARDING_LABEL_PRINT_OPTIONS = {
  paper: LABEL_PAPER_BOARDING_50X30,
  barWidth: 1,
  barHeight: 46,
  margin: 2,
  /** The digits jsbarcode draws under the bars — the human-readable fallback when a scan fails. */
  fontSize: 8,
  guardianFontSize: 8,
  petFontSize: 10,
} as const;

/**
 * Full `BarcodeLabelPrint` prop set for an item label, so all three item screens
 * print an identical sticker and cannot drift apart.
 *
 * WHAT THE STICKER CARRIES: the Arabic item name, the English item name, the bars,
 * and the digits JsBarcode draws under them. Nothing else.
 *
 * ⚠️ THE CLINIC NAME AND THE UNIT WERE DELIBERATELY REMOVED. Both were context, not
 * identification — every sticker on the shelf named the same clinic, and the unit is
 * on the box itself — while the name an Arabic-speaking picker actually reads was
 * missing. `heading` carries the ARABIC name (that slot already renders `dir="auto"`,
 * which is what keeps a mixed name — Arabic words plus a Latin brand or size — in the
 * right order) and `title` carries the English one. An item with no Arabic name simply
 * prints the English line; there is no fallback, by the same rule that keeps
 * `ItemDefinition.arabicName` from borrowing `item_name`.
 *
 * The sizes are not cosmetic. 25 mm is ~94 px of printable height, ~79 px of it usable
 * after the 2 mm padding, and the stack budgets roughly 9.9 + 9.9 (the two name lines)
 * + 47 (bars 32 + 2x2 margin + 9 digits + jsbarcode's own 2 px text margin) + 4 (two
 * 2 px gaps) ≈ 71 px. BarcodeLabelPrint's 80 x 50 mm defaults (barHeight 60,
 * fontSize 16) overflow it and the sticker prints clipped.
 *
 * `barWidth: 1` matters just as much horizontally: a CODE128 of a typical item
 * barcode at width 2 is wider than 50 mm, and the print CSS scales an oversized
 * SVG down through `max-width:100%` — shrinking the bars' HEIGHT with it until
 * they are too short to scan reliably.
 */
export const ITEM_LABEL_PRINT_PROPS = {
  paper: LABEL_PAPER_ITEM_50X25,
  barWidth: 1,
  // Raised from 28 with the space the clinic-name and unit lines gave back. Taller bars
  // are read more reliably by a hand scanner held at an angle, which is how a shelf is
  // actually scanned.
  barHeight: 32,
  margin: 2,
  fontSize: 9,
  // Both names at the SAME size: they are one item name in two languages, and making
  // either smaller would read as a subtitle rather than the item's other name.
  headingFontSize: 9,
  titleFontSize: 9,
  subtitleFontSize: 7,
} as const;

/**
 * QR rendering options for printed labels.
 *
 * Error correction stays at "H" (30% recovery) because these stickers live on
 * kennel doors and pet tags — they get scuffed, wet and partially peeled.
 * Colours are deliberately pure black on white: scanners need dark modules on a
 * light quiet zone, so the label never follows the app theme (same reasoning
 * documented in BarcodeDisplay.vue).
 */
export const QR_LABEL_OPTIONS = {
  size: envNumber(import.meta.env.VITE_QR_LABEL_SIZE, 220),
  errorCorrectionLevel: envValue(import.meta.env.VITE_QR_ERROR_CORRECTION) || "H",
  margin: envNumber(import.meta.env.VITE_QR_MARGIN, 2),
  background: "#ffffff",
  foreground: "#000000",
} as const;

/** In-app route that resolves a scanned code. */
export const QR_SEARCH_PATH = "/qr-search";

/** Scannable entity kinds. Extend here when a new code type is introduced. */
export type ScannedCodeKind = "pet" | "room" | "boarding" | "driver";

export interface ScannedCode {
  kind: ScannedCodeKind;
  id: string;
}

/**
 * Public landing page a stranger reaches when scanning a lost pet's tag.
 * Kept external and configurable: the pet tag must stay useful to someone who
 * finds the animal and has no access to this staff app.
 */
export const LOST_PET_BASE_URL
  = envValue(import.meta.env.VITE_LOST_PET_URL) || "https://myheroteam.xyz/lost.html";

/** Query parameter carrying the pet id on the lost-pet landing URL. */
export const PET_QR_QUERY_PARAM = envValue(import.meta.env.VITE_LOST_PET_QUERY_PARAM) || "pet";

/**
 * Id prefixes used to recognise a bare typed/scanned code without a lookup.
 * `Service Room` ids look like `ROOM-001` (see serviceRoomApi.buildRoomId).
 */
export const PET_ID_PREFIXES = ["PET-", "PET_"] as const;
export const ROOM_ID_PREFIXES = ["ROOM-", "ROOM_"] as const;

/**
 * `Pet Boarding` name prefixes, for the cage sticker printed by `boardingLabelPrinter`.
 *
 * ⚠️ THIS EXISTS TO STOP A MISPARSE, not merely to add a feature. A boarding name like
 * `BRD-00055` is a short alphanumeric containing a letter, which is exactly what the bare
 * room-code branch of `parseScannedCode` matches — so without this, scanning a boarding
 * sticker fires a lookup for a room that does not exist. The boarding branch therefore runs
 * BEFORE that fallback.
 *
 * ⚠️ AND THE SERIES IS NOT CERTAIN. The backend docs show `BRD-00044`, `PB-2026-00147` and
 * `BOARD-009` at different dates, and the mock uses `BOARD-001`; none of them is safe to
 * hardcode alone. All four ship as defaults and a deployment whose series differs sets
 * `VITE_BOARDING_ID_PREFIXES` rather than editing this file. None can collide with a room:
 * room ids are `ROOM-*` and room codes look like `A-01`.
 */
export const BOARDING_ID_PREFIXES: readonly string[]
  = (envValue(import.meta.env.VITE_BOARDING_ID_PREFIXES) || "BRD-,BRD_,PB-,BOARD-")
    .split(",")
    .map(prefix => prefix.trim().toUpperCase())
    .filter(Boolean);

/**
 * `Driver` name prefixes — the driver's personal QR (`/drivers/me` → "My code").
 *
 * ⚠️ SAME MISPARSE AS BOARDING. A driver id like `HR-DRI-2026-00021` is an alphanumeric with a
 * letter, which the bare-room fallback of `parseScannedCode` would swallow as a room code. The
 * driver branch therefore runs BEFORE that fallback. Override with `VITE_DRIVER_ID_PREFIXES`.
 */
export const DRIVER_ID_PREFIXES: readonly string[]
  = (envValue(import.meta.env.VITE_DRIVER_ID_PREFIXES) || "HR-DRI-,DRV-")
    .split(",")
    .map(prefix => prefix.trim().toUpperCase())
    .filter(Boolean);

/**
 * Build the value encoded in a PET QR code.
 *
 * The pet id travels as a query parameter on the public lost-pet page, so one
 * printed tag serves both audiences: a stranger scanning it lands on the public
 * page, while staff scanning it in-app resolve the pet. Previously this payload
 * was a constant URL with no id, making every pet's QR identical.
 */
export function buildPetQrUrl(petId: string): string {
  const id = String(petId ?? "").trim();
  if (!id)
    return LOST_PET_BASE_URL;

  const separator = LOST_PET_BASE_URL.includes("?") ? "&" : "?";

  return `${LOST_PET_BASE_URL}${separator}${PET_QR_QUERY_PARAM}=${encodeURIComponent(id)}`;
}

/**
 * Build the value encoded in a SERVICE ROOM QR code — an absolute deep link to
 * this app. `window.location.origin` is resolved at call time so the same
 * sticker works from a LAN IP, a domain, or an Electron build without rebuilds.
 *
 * A URL (not a bare id) is used so the code stays meaningful to any phone
 * camera, and a room's reservation data — which changes daily — is always
 * fetched live rather than frozen into the printed label.
 */
export function buildServiceRoomQrUrl(roomId: string): string {
  const id = encodeURIComponent(String(roomId ?? "").trim());
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return `${origin}${QR_SEARCH_PATH}/room/${id}`;
}

/**
 * Build the value encoded in a DRIVER's personal QR — a deep link, like the service room's.
 * Scanned in the app it opens that driver's settlement; opened by any phone camera it lands on
 * `/qr-search/driver/<id>`, which redirects there too. The id alone also parses when scanned bare.
 */
export function buildDriverQrUrl(driverId: string): string {
  const id = encodeURIComponent(String(driverId ?? "").trim());
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return `${origin}${QR_SEARCH_PATH}/driver/${id}`;
}

function startsWithAny(value: string, prefixes: readonly string[]) {
  const upper = value.toUpperCase();

  return prefixes.some(prefix => upper.startsWith(prefix));
}

/**
 * Parse anything a scanner or a human can produce into a typed code.
 *
 * Deliberately liberal — staff will point this at product barcodes, foreign QR
 * codes and hand-typed room codes. Accepts:
 *   - a pet QR URL           (…/lost.html?pet=PET-001)
 *   - a room deep link       (http://host/qr-search/room/ROOM-001, or the path)
 *   - a bare prefixed id     (PET-001, ROOM-001)
 *   - a bare room code       (A-01) — rooms are matched by code as well as id
 *
 * Returns null for anything unrecognised so the caller can show a clean
 * "unrecognised code" message instead of firing a bogus lookup.
 */
export function parseScannedCode(text: string): ScannedCode | null {
  const raw = String(text ?? "").trim();
  if (!raw)
    return null;

  // 1. Try to read it as a URL (absolute, or a bare path we can qualify).
  const asUrl = toUrl(raw);
  if (asUrl) {
    const petId = asUrl.searchParams.get(PET_QR_QUERY_PARAM)?.trim();
    if (petId)
      return { kind: "pet", id: petId };

    // …/qr-search/<kind>/<id>
    const segments = asUrl.pathname.split("/").filter(Boolean);
    const searchIndex = segments.indexOf(QR_SEARCH_PATH.replace(/^\//, ""));
    if (searchIndex !== -1) {
      const kind = segments[searchIndex + 1];
      const id = segments[searchIndex + 2];
      if (id && (kind === "room" || kind === "pet" || kind === "boarding" || kind === "driver"))
        return { kind, id: safeDecode(id) };
    }

    // A URL we do not recognise is NOT a code — do not fall through to the
    // bare-id branch, or "https://example.com" would be treated as a room code.
    return null;
  }

  // 2. Bare ids by prefix.
  if (startsWithAny(raw, PET_ID_PREFIXES))
    return { kind: "pet", id: raw };

  if (startsWithAny(raw, ROOM_ID_PREFIXES))
    return { kind: "room", id: raw };

  // 2b. A boarding sticker. MUST be tested before the bare-room-code fallback below, which
  //     would otherwise swallow `BRD-00055` and fire a room lookup — see BOARDING_ID_PREFIXES.
  if (startsWithAny(raw, BOARDING_ID_PREFIXES))
    return { kind: "boarding", id: raw };

  // 2c. A driver's personal code — also BEFORE the room fallback (see DRIVER_ID_PREFIXES).
  if (startsWithAny(raw, DRIVER_ID_PREFIXES))
    return { kind: "driver", id: raw };

  // 3. A bare room code such as "A-01". Room codes always contain a letter, which
  //    is what separates them from an all-digit product barcode — without that
  //    guard, scanning an EAN-13 item at this screen would fire a room lookup.
  if (/^[\w-]{1,32}$/.test(raw) && /[a-z]/i.test(raw))
    return { kind: "room", id: raw };

  return null;
}

/**
 * Is this scan a DRIVER's personal code? Returns the driver id, or null.
 *
 * ⚠️ FOR ITEM SCANNERS (POS). They must not call `parseScannedCode` whole: its bare-room
 * fallback matches any alphanumeric product barcode. This asks the one question they need.
 */
export function matchDriverCode(text: string): string | null {
  const parsed = parseScannedCode(text);

  return parsed?.kind === "driver" ? parsed.id : null;
}

/**
 * Where a scanned code that is a PAGE (not a pop-up result) should take the user. Shared by the
 * QR overlay and the `/qr-search` page so the two cannot route differently. `null` = not a
 * page — show the in-place result (pet / room).
 */
export function scannedCodeRoute(code: ScannedCode): string | null {
  if (code.kind === "boarding")
    return `/healthcare/boarding/${encodeURIComponent(code.id)}`;

  if (code.kind === "driver")
    return `/drivers/settlement?driver=${encodeURIComponent(code.id)}&settle=1`;

  return null;
}

function toUrl(value: string): URL | null {
  const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";

  if (/^https?:\/\//i.test(value)) {
    try {
      return new URL(value);
    }
    catch {
      return null;
    }
  }

  // A bare path such as "/qr-search/room/ROOM-001".
  if (value.startsWith("/")) {
    try {
      return new URL(value, origin);
    }
    catch {
      return null;
    }
  }

  return null;
}

function safeDecode(value: string) {
  try {
    return decodeURIComponent(value);
  }
  catch {
    return value;
  }
}
